import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, RotateCcw, ShieldCheck, FileText, CalendarDays, Users, ReceiptEuro, Mail, BellRing, Power, Truck, Mic, Globe } from "lucide-react";
import api, { API } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/format";
import { getLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ChatMessage } from "@/components/agent/ChatMessage";
import { BriefingCard } from "@/components/agent/BriefingCard";
import { VoiceButton } from "@/components/agent/VoiceButton";

const SUGGESTIONS = [
  "Riepilogo di oggi: appuntamenti, cantieri e pagamenti",
  "Leggi le ultime email e dimmi a quali devo rispondere",
  "Crea un cantiere da lunedì a fine mese con 3 operai",
  "Ricordami l'F24 ogni 16 del mese",
  "Prepara un DDT dall'ultimo preventivo accettato",
  "Quanto costa oggi un sacco di cemento da 25 kg?",
];
const SUGGESTIONS_SIMPLE = [
  "Cosa ho in agenda questa settimana?",
  "Fissa un appuntamento domani alle 9 con Rossi",
  "Leggi le ultime email e dimmi a quali devo rispondere",
  "Ci sono messaggi WhatsApp non letti?",
  "Ricordami l'F24 ogni 16 del mese",
  "Quali bonus edilizi ci sono quest'anno?",
];
const COMMON = [
  { icon: Mic, text: "Parla invece di scrivere: tieni premuto il microfono" },
  { icon: Globe, text: "Cerca informazioni aggiornate su internet (prezzi, normative, bonus)" },
];
const FEATURES = [
  { icon: FileText, text: "Prepara e modifica preventivi (anche garanzia) con il tuo listino e i preziari" },
  { icon: CalendarDays, text: "Gestisce agenda, cantieri, operai e ore lavorate" },
  { icon: BellRing, text: "Promemoria pagamenti (rate, tasse) e riepilogo ogni mattina alle 7:30" },
  { icon: Mail, text: "Legge la posta e WhatsApp, risponde e invia email con la tua firma" },
  { icon: Truck, text: "Crea DDT / bolle anche partendo da un preventivo" },
  { icon: ReceiptEuro, text: "Rate fatture, acconti, spese e listino prezzi" },
  { icon: Users, text: "Aggiunge e aggiorna clienti e fornitori" },
  ...COMMON,
];
const FEATURES_SIMPLE = [
  { icon: CalendarDays, text: "Fissa, sposta e cancella appuntamenti e promemoria" },
  { icon: BellRing, text: "Promemoria pagamenti (rate, tasse) e riepilogo ogni mattina alle 7:30" },
  { icon: Users, text: "Aggiunge e aggiorna i clienti in rubrica" },
  { icon: Mail, text: "Legge e risponde a email e messaggi WhatsApp" },
  ...COMMON,
];

const EnableCard = ({ onEnable, busy, features }) => (
  <div className="max-w-2xl mx-auto rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-6 animate-in-up" data-testid="agent-enable-card">
    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-orange-400 text-white flex items-center justify-center"><Sparkles className="w-6 h-6" /></div>
    <div>
      <h2 className="font-heading text-2xl font-extrabold tracking-tight">Il tuo assistente IA</h2>
      <p className="text-muted-foreground mt-1.5">Scrivi cosa ti serve in parole semplici: l'assistente cerca i dati nel gestionale e prepara il lavoro per te.</p>
    </div>
    <ul className="grid sm:grid-cols-2 gap-3">
      {features.map((f) => <li key={f.text} className="flex gap-2.5 text-sm"><f.icon className="w-4 h-4 text-primary mt-0.5 shrink-0" />{f.text}</li>)}
    </ul>
    <div className="rounded-xl bg-secondary/60 p-4 text-sm flex gap-3"><ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />Prima di creare, modificare o inviare qualcosa ti chiede sempre «Confermi?». Puoi disattivarlo quando vuoi.</div>
    <Button onClick={onEnable} disabled={busy} className="h-11" data-testid="agent-enable-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Power className="w-4 h-4 mr-2" /> Attiva l'assistente</>}</Button>
  </div>
);

async function streamChat(text, onEvent) {
  const res = await fetch(`${API}/agent/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
    body: JSON.stringify({ text, lang: getLang(), tz: Intl.DateTimeFormat().resolvedOptions().timeZone }),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(formatApiErrorDetail(j.detail));
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i;
    while ((i = buf.indexOf("\n\n")) >= 0) {
      const line = buf.slice(0, i);
      buf = buf.slice(i + 2);
      if (line.startsWith("data: ")) onEvent(JSON.parse(line.slice(6)));
    }
  }
}

export default function Assistente() {
  const [cfg, setCfg] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const [toggling, setToggling] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { api.get("/agent/settings").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false })); }, []);
  useEffect(() => { if (cfg?.enabled) api.get("/agent/messages").then((r) => setMessages(r.data)).catch(() => {}); }, [cfg?.enabled]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages, draft]);

  const toggle = async (enabled) => {
    setToggling(true);
    try { setCfg((await api.put("/agent/settings", { enabled })).data); toast.success(enabled ? "Assistente attivato" : "Assistente disattivato"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setToggling(false); }
  };

  const send = async (raw) => {
    const msg = (raw ?? text).trim();
    if (!msg || sending) return;
    setText(""); setSending(true);
    setMessages((l) => [...l, { id: "tmp-user", role: "user", text: msg }]);
    setDraft({ id: "draft", role: "assistant", text: "", actions: [], streaming: true, status: "" });
    try {
      await streamChat(msg, (ev) => {
        if (ev.type === "user") setMessages((l) => l.map((m) => (m.id === "tmp-user" ? ev.message : m)));
        else if (ev.type === "delta") setDraft((d) => ({ ...d, text: d.text + ev.text, status: "" }));
        else if (ev.type === "status") setDraft((d) => ({ ...d, status: ev.text }));
        else if (ev.type === "action") setDraft((d) => ({ ...d, actions: [...d.actions, ev.action], status: "" }));
        else if (ev.type === "result") setMessages((l) => [...l.map((m) => ({ ...m, actions: (m.actions || []).map((a) => (a.status === "pending" ? { ...a, status: ev.message.result?.ok ? "done" : "failed" } : a)) })), ev.message]);
        else if (ev.type === "error") toast.error(ev.detail);
        else if (ev.type === "done" && ev.message) setMessages((l) => [...l, ev.message]);
      });
    } catch (e) { toast.error(e.message); setMessages((l) => l.filter((m) => m.id !== "tmp-user")); }
    finally { setDraft(null); setSending(false); }
  };

  const markAction = (id, status) => setMessages((l) => l.map((m) => ({ ...m, actions: (m.actions || []).map((a) => (a.id === id ? { ...a, status } : a)) })));
  const confirm = async (a) => {
    setBusyAction(a.id);
    try {
      const r = await api.post(`/agent/actions/${a.id}/confirm`);
      markAction(a.id, r.data.result?.ok ? "done" : "failed");
      setMessages((l) => [...l, r.data]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusyAction(null); }
  };
  const cancel = async (a) => {
    await api.post(`/agent/actions/${a.id}/cancel`).catch(() => {});
    markAction(a.id, "cancelled");
  };
  const clear = async () => { await api.delete("/agent/messages"); setMessages([]); };

  if (!cfg) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  const simple = cfg.edition === "semplice";
  if (!cfg.enabled) return <EnableCard onEnable={() => toggle(true)} busy={toggling} features={simple ? FEATURES_SIMPLE : FEATURES} />;
  const suggestions = simple ? SUGGESTIONS_SIMPLE : SUGGESTIONS;

  const all = draft ? [...messages, draft] : messages;
  const lastMsg = messages[messages.length - 1];
  const quickId = lastMsg?.role === "assistant" && lastMsg.kind !== "result" ? lastMsg.id : null;
  return (
    <div className="flex flex-col h-[calc(100vh-9.5rem)] min-h-[520px] rounded-2xl border border-border bg-card overflow-hidden" data-testid="agent-page">
      <div className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-border">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-orange-400 text-white flex items-center justify-center"><Sparkles className="w-[18px] h-[18px]" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold leading-tight">Assistente IA <span className="ml-1 align-middle text-[10px] font-semibold uppercase tracking-wider text-primary" data-testid="agent-model-label">{cfg.model}</span></div>
          <div className="text-xs text-muted-foreground truncate">{cfg.mailbox ? `Casella collegata: ${cfg.mailbox}` : `Risposte email a: ${cfg.email || "imposta l'email in Impostazioni"}`}</div>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground"><Switch checked={cfg.enabled} onCheckedChange={toggle} disabled={toggling} data-testid="agent-enabled-switch" /> Attivo</label>
        <Button variant="ghost" size="sm" onClick={clear} disabled={sending} data-testid="agent-clear-button"><RotateCcw className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Nuova chat</span></Button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-5" data-testid="agent-messages">
        {all.length === 0 && (
          <div className="max-w-xl mx-auto text-center pt-2 space-y-5">
            <div className="text-left"><BriefingCard /></div>
            <div className="font-heading text-2xl font-extrabold tracking-tight">Ciao! Come posso aiutarti oggi?</div>
            <div className="grid sm:grid-cols-2 gap-2 text-left">
              {suggestions.map((s, i) => (
                <button key={s} onClick={() => send(s)} className="rounded-xl border border-border p-3 text-sm hover:bg-secondary hover:border-primary/40 transition-colors" data-testid={`agent-suggestion-${i}`}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {all.map((m) => <ChatMessage key={m.id} m={m} busyAction={busyAction} onConfirm={confirm} onCancel={cancel}
          onQuick={!sending && m.id === quickId ? (t) => send(t) : undefined} />)}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-border p-3 sm:p-4 flex items-end gap-2">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={1} placeholder="Scrivi o tieni premuto il microfono e parla… es. «Fissa un appuntamento domani alle 9»"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="min-h-[44px] max-h-40 resize-none" data-testid="agent-input" />
        <VoiceButton onText={(t) => send(t)} disabled={sending} showHint={all.length === 0} />
        <Button type="submit" size="icon" className="h-11 w-11 shrink-0" disabled={sending || !text.trim()} data-testid="agent-send-button">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
    </div>
  );
}
